import React, { useCallback, useMemo } from 'react';
import { PosWheel, PosWheelColumn } from '../../../../components/PosWheel/PosWheel';
import classes from './pos-wheel-date-picker.module.scss';

const HEBREW_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

export type WheelDate = { year: number; month: number; day: number };

export type PosWheelDatePickerProps = {
  // Allowed dates (a date with no closed session is not selectable).
  allowed: WheelDate[];
  value: WheelDate | null;
  onChange: (next: WheelDate) => void;
};

// Summary: Drum/wheel date picker — 3 scroll columns (year/month/day) constrained to allowed dates.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
export const PosWheelDatePicker: React.FC<PosWheelDatePickerProps> = ({ allowed, value, onChange }) => {
  // Fall back to the first allowed date so columns render before value is seeded (avoids flicker).
  const selected = value ?? allowed[0] ?? null;

  const years = useMemo(() => Array.from(new Set(allowed.map((d) => d.year))).sort((a, b) => a - b), [allowed]);

  const months = useMemo(() => {
    if (!selected) return [] as number[];
    return Array.from(new Set(allowed.filter((d) => d.year === selected.year).map((d) => d.month))).sort((a, b) => a - b);
  }, [allowed, selected]);

  const days = useMemo(() => {
    if (!selected) return [] as number[];
    return Array.from(
      new Set(allowed.filter((d) => d.year === selected.year && d.month === selected.month).map((d) => d.day)),
    ).sort((a, b) => a - b);
  }, [allowed, selected]);

  // Pick the first allowed date in a year/month so partial picks resolve to a valid date.
  const pickValid = useCallback(
    (partial: Partial<WheelDate>): WheelDate | null => {
      const candidates = allowed.filter(
        (d) =>
          (partial.year === undefined || d.year === partial.year) &&
          (partial.month === undefined || d.month === partial.month) &&
          (partial.day === undefined || d.day === partial.day),
      );
      candidates.sort((a, b) => a.year - b.year || a.month - b.month || a.day - b.day);
      return candidates[0] ?? null;
    },
    [allowed],
  );

  if (allowed.length === 0 || !selected) {
    return <div className={classes.empty}>אין תאריכים זמינים</div>;
  }

  return (
    <PosWheel>
      <PosWheelColumn
        items={years.map((y) => ({
          key: `y-${y}`,
          label: String(y),
          selected: y === selected.year,
          testid: `report-date-year-${y}`,
          onClick: () => { const next = pickValid({ year: y }); if (next) onChange(next); },
        }))}
      />
      <PosWheelColumn
        items={months.map((m) => ({
          key: `m-${m}`,
          label: HEBREW_MONTHS[m - 1],
          selected: m === selected.month,
          testid: `report-date-month-${m}`,
          onClick: () => { const next = pickValid({ year: selected.year, month: m }); if (next) onChange(next); },
        }))}
      />
      <PosWheelColumn
        items={days.map((d) => ({
          key: `d-${d}`,
          label: String(d),
          selected: d === selected.day,
          testid: `report-date-day-${d}`,
          onClick: () => { const next = pickValid({ year: selected.year, month: selected.month, day: d }); if (next) onChange(next); },
        }))}
      />
    </PosWheel>
  );
};
